import React, { useMemo, useState, useEffect } from 'react'
import {
  Card,
  Row,
  Col,
  Table,
  Select,
  DatePicker,
  Button,
  Statistic,
  Empty,
  Spin,
  message,
  Input,
} from 'antd'
import {
  AppstoreOutlined,
  TableOutlined,
  DollarOutlined,
  FileDoneOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons'
import BillService from '../../../../services/Billservice'
import './TableReport.css'
import { notify } from '../../../../components/Customs/NotifyProvider'
import { canViewModule, canCreateModule, canUpdateModule, canDeleteModule } from '../../../../utils/permissionUtils'
const { RangePicker } = DatePicker

const TableReport = () => {
  const MODULE_KEY = 'table_report'
  const canView = canViewModule(MODULE_KEY)
  const canCreate = canCreateModule(MODULE_KEY)
  const canUpdate = canUpdateModule(MODULE_KEY)
  const canDelete = canDeleteModule(MODULE_KEY)
  const isViewOnly = canView && !canCreate && !canUpdate && !canDelete
  const [loading, setLoading] = useState(false)
  const [tableRowsRaw, setTableRowsRaw] = useState([])
  const [dateFilter, setDateFilter] = useState('today')
  const [range, setRange] = useState(null)
  const [zoneFilter, setZoneFilter] = useState('All')
  const [searchText, setSearchText] = useState('')

  const buildFilters = () => {
    if (isViewOnly) return { date: 'today' }
    const filters = {}

    if (dateFilter === 'custom' && range && range.length === 2) {
      filters.from = range[0].startOf('day').format('YYYY-MM-DD HH:mm:ss')
      filters.to = range[1].endOf('day').format('YYYY-MM-DD HH:mm:ss')
    } else if (dateFilter === 'today') {
      filters.date = 'today'
    } else if (dateFilter === 'yesterday') {
      filters.date = 'yesterday'
    }

    if (zoneFilter !== 'All') {
      filters.zone = zoneFilter
    }

    return filters
  }

  const fetchData = async () => {
    if (!canView) return
    setLoading(true)
    try {
      const filters = buildFilters()
      const [tablesRes, billsRes] = await Promise.all([
        BillService.getAllTables(),
        BillService.getBillReport({ ...filters, status: 'closed' }),
      ])

      const tables = Array.isArray(tablesRes) ? tablesRes : []
      const bills = Array.isArray(billsRes) ? billsRes : []

      const groupedByTable = bills.reduce((acc, bill) => {
        const tableId = Number(bill.table_id)
        if (!tableId) return acc

        if (!acc[tableId]) {
          acc[tableId] = {
            bills_count: 0,
            item_count: 0,
            total_discount: 0,
            total_revenue: 0,
          }
        }

        const items = Array.isArray(bill.items) ? bill.items : []
        const discount = Number(bill.discount || 0)
        const finalTotal = Number(bill.final_total || 0)

        acc[tableId].bills_count += 1
        acc[tableId].item_count += items.length
        acc[tableId].total_discount += discount
        acc[tableId].total_revenue += finalTotal

        return acc
      }, {})

      const rows = tables.map((table) => {
        const summary = groupedByTable[Number(table.id)] || {
          bills_count: 0,
          item_count: 0,
          total_discount: 0,
          total_revenue: 0,
        }

        return {
          table_id: table.id,
          table_name: table.name,
          zone: table.zone || '-',
          capacity: table.capacity,
          status: table.status,
          ...summary,
        }
      })

      setTableRowsRaw(rows)
    } catch (err) {
      notify.error(`Failed to load table report: ${err.message}`)
      setTableRowsRaw([])
    } finally {
      setLoading(false)
    }
  }

  //auto today data
  useEffect(() => {
  fetchData()
}, [])

  const handleClear = () => {
    if (!canUpdate && !canDelete) {
      notify.error('Permission Denied', 'No permission to clear table report filters')
      return
    }
    setDateFilter('today')
    setRange(null)
    setZoneFilter('All')
    setSearchText('')
    setTableRowsRaw([])
    
  }

  const zoneOptions = useMemo(() => {
    const zonesFromTables = (tableRowsRaw || []).map((t) => t.zone).filter(Boolean)
    return ['All', ...new Set(zonesFromTables)]
  }, [tableRowsRaw])

  const tableRows = useMemo(() => {
    const search = searchText.trim().toLowerCase()

    return (tableRowsRaw || [])
      .map((row) => {
        const totalRevenue = Number(row.total_revenue || 0)
        const billsCount = Number(row.bills_count || 0)

        return {
          ...row,
          key: row.table_id,
          total_revenue: totalRevenue,
          bills_count: billsCount,
          item_count: Number(row.item_count || 0),
          total_discount: Number(row.total_discount || 0),
          avg_bill_value: billsCount > 0 ? totalRevenue / billsCount : 0,
        }
      })
      .filter((row) => {
        if (!search) return true

        return [
          row.table_name,
          row.zone,
          row.status,
          String(row.capacity),
          String(row.bills_count),
          String(row.item_count),
          String(row.total_revenue),
          String(row.total_discount),
        ]
          .join(' ')
          .toLowerCase()
          .includes(search)
      })
  }, [tableRowsRaw, searchText])

  const summary = useMemo(() => {
    const totalTables = tableRows.length
    const activeTables = tableRows.filter((row) => Number(row.bills_count || 0) > 0).length
    const totalBills = tableRows.reduce((sum, row) => sum + Number(row.bills_count || 0), 0)
    const totalRevenue = tableRows.reduce(
      (sum, row) => sum + Number(row.total_revenue || 0),
      0
    )

    return {
      totalTables,
      activeTables,
      totalBills,
      totalRevenue,
    }
  }, [tableRows])

  const mainColumns = [
    {
      title: 'Table',
      dataIndex: 'table_name',
      key: 'table_name',
      render: (value, record) => (
        <div className="table-main-primary">
          <div className="table-main-title">{value || '-'}</div>
          <div className="table-main-subtitle">
            Table ID: {record.table_id || '-'}
          </div>
        </div>
      ),
    },
    {
      title: 'Zone',
      dataIndex: 'zone',
      key: 'zone',
      render: (zone) => <span className="table-zone-pill">{zone || '-'}</span>,
    },
    {
      title: 'Capacity',
      dataIndex: 'capacity',
      key: 'capacity',
      align: 'center',
      render: (value) => <strong>{value || 0}</strong>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const normalizedStatus = String(status || '').toLowerCase()
        const isOccupied = normalizedStatus === 'occupied'
        return (
          <span className={`table-status-pill ${isOccupied ? 'occupied' : 'available'}`}>
            {normalizedStatus ? normalizedStatus.toUpperCase() : '-'}
          </span>
        )
      },
    },
    {
      title: 'Bills',
      dataIndex: 'bills_count',
      key: 'bills_count',
      align: 'center',
      render: (value) => Number(value || 0),
    },
    {
      title: 'Items',
      dataIndex: 'item_count',
      key: 'item_count',
      align: 'center',
      render: (value) => Number(value || 0),
    },
    {
      title: 'Discount',
      dataIndex: 'total_discount',
      key: 'total_discount',
      align: 'right',
      render: (value) => (
        <span className={Number(value || 0) > 0 ? 'table-discount-text' : ''}>
          ₹{Number(value || 0).toFixed(2)}
        </span>
      ),
    },
    {
      title: 'Total Revenue',
      dataIndex: 'total_revenue',
      key: 'total_revenue',
      align: 'right',
      render: (value) => (
        <strong className="table-total-text">
          ₹{Number(value || 0).toFixed(2)}
        </strong>
      ),
    },
    {
      title: 'Avg Bill Value',
      dataIndex: 'avg_bill_value',
      key: 'avg_bill_value',
      align: 'right',
      render: (value) => `₹${Number(value || 0).toFixed(2)}`,
    },
  ]

  return !canView ? (
    <Card>
      <Empty description="No permission to view table report" />
    </Card>
  ) : (
    <div className="table-report-page">
      <div className="table-report-hero">
        <div className="table-report-hero-left">
          <div className="table-report-hero-icon">
            <TableOutlined />
          </div>
          <div>
            <h2 className="table-report-hero-title">Table Report</h2>
            <p className="table-report-hero-subtitle">
              Modern hotel-style table analytics and revenue overview
            </p>
          </div>
        </div>
      </div>

      <Card className="table-report-card">
        <Row gutter={[12, 12]} className="table-report-filters">
          <Col xs={24} sm={12} md={8} lg={5}>
            <Select
              value={dateFilter}
              onChange={setDateFilter}
              disabled={isViewOnly}
              options={[
                { label: 'Today', value: 'today' },
                { label: 'Yesterday', value: 'yesterday' },
                { label: 'All', value: 'all' },
                { label: 'Custom Range', value: 'custom' },
              ]}
            />
          </Col>

          {dateFilter === 'custom' && !isViewOnly && (
            <Col xs={24} sm={12} md={10} lg={7}>
              <RangePicker
                style={{ width: '100%' }}
                value={range}
                onChange={(vals) => setRange(vals)}
              />
            </Col>
          )}

          <Col xs={24} sm={12} md={8} lg={5}>
            <Select
              value={zoneFilter}
              onChange={setZoneFilter}
              disabled={isViewOnly}
              options={zoneOptions.map((z) => ({
                label: z,
                value: z,
              }))}
            />
          </Col>

          <Col xs={24} sm={12} md={6} lg={3}>
            <Button
              type="primary"
              block
              icon={<ReloadOutlined />}
              className="table-report-load-btn"
              onClick={fetchData}
              loading={loading}
              disabled={!canView && !canCreate}
            >
              Load
            </Button>
          </Col>

          <Col xs={24} sm={12} md={6} lg={4}>
            <Button
              block
              className="table-report-clear-btn"
              onClick={handleClear}
              disabled={!canUpdate && !canDelete}
            >
              Clear
            </Button>
          </Col>
        </Row>

        <div className="table-report-toolbar">
          <Input
            allowClear
            placeholder="Search within loaded rows..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="table-report-search"
          />
        </div>

        <Row gutter={[16, 16]} className="table-report-stats">
          <Col xs={24} sm={12} md={6}>
            <Card className="table-report-stat-card">
              <Statistic
                title="Total Tables"
                value={summary.totalTables}
                prefix={<TableOutlined />}
              />
            </Card>
          </Col>

          <Col xs={24} sm={12} md={6}>
            <Card className="table-report-stat-card">
              <Statistic
                title="Tables With Bills"
                value={summary.activeTables}
                prefix={<AppstoreOutlined />}
              />
            </Card>
          </Col>

          <Col xs={24} sm={12} md={6}>
            <Card className="table-report-stat-card">
              <Statistic
                title="Total Bills"
                value={summary.totalBills}
                prefix={<FileDoneOutlined />}
              />
            </Card>
          </Col>

          <Col xs={24} sm={12} md={6}>
            <Card className="table-report-stat-card">
              <Statistic
                title="Total Revenue"
                value={summary.totalRevenue}
                precision={2}
                prefix={<DollarOutlined />}
                formatter={(value) => `₹${Number(value || 0).toFixed(2)}`}
              />
            </Card>
          </Col>
        </Row>

        <Spin spinning={loading}>
          {!tableRows.length ? (
            <div className="table-report-empty">
              <Empty description="No table report data found" />
            </div>
          ) : (
            <div className="table-report-modern-table">
              <Table
                rowKey="table_id"
                columns={mainColumns}
                dataSource={tableRows}
                pagination={{ pageSize: 10 }}
              />
            </div>
          )}
        </Spin>
      </Card>
    </div>
  )
}

export default TableReport
